import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CardContent,
  TextField,
  Autocomplete,
  Card,
  Grid,
  alpha,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
DEFECT_TYPES,
VALIDATION_MESSAGES,
ALERT_MESSAGES,
MobileHelpModalMessages,
} from '../../../ProductionManagement/KPIProductionResult/constants';
import { DateTimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ko from "date-fns/locale/ko";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { MobileHelpModal } from '../../../Common/HelpModal';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Swal from 'sweetalert2';
import { generateRandomProductionData } from '../utils/randomDataGenerator';
import useLocalStorageVO from '../../../Common/UseLocalStorageVO';

const ProductionResultForm = ({
  initialData,
  productOptions,
  equipmentOptions,
  warehouseOptions,
  unitOptions,
  vendorOptions,
  kpiSubscriptions, // KPI 구독 정보 추가
  onSave,
  getBorderColor,
  theme,
  isDarkMode,
  getTextColor,
  getBgColor,
}) => {

    // 사용자 정보 가져오기
    const { loginUser } = useLocalStorageVO();

    // 선택된 장비 상태
    const [selectedEquipment, setSelectedEquipment] = useState(equipmentOptions[0] || null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // 도움말 모달
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

    // 자동 채우기 완료 여부 (화면 진입 시 한 번만 실행)
    const [isAutoFilled, setIsAutoFilled] = useState(false);

    // 생산실적 데이터 초기값
    const getInitialProductionData = () => ({
      id: '',
      equipmentId: '',
      productId: '',
      warehouseId: '',
      goodQty: 0,
      defectQty: 0,
      prodStartTime:  new Date() ,
      prodEndTime:  new Date() ,
      flagActive: true,
      customerId: '',
      orderDate:  new Date() ,
      deliveryDate:  new Date() ,
      unitPrice: 0,
      autoShipment: true
    });


    // 생산실적 데이터 상태
    const [productionData, setProductionData] = useState(() => getInitialProductionData());

    // 유효성 검증 상태
    const [validationErrors, setValidationErrors] = useState({});
    const [isValid, setIsValid] = useState(false);


    // 불량정보 상태
    const [defectInfos, setDefectInfos] = useState([]);

    // 제품 선택 핸들러
    const handleProductChange = useCallback((event, newValue) => {
      setSelectedProduct(newValue);
      setProductionData(prev => ({
        ...prev,
        productId: newValue ? newValue.systemMaterialId : ''
      }));
    }, []);

    // 날짜 변경 핸들러
    const handleDateChange = useCallback((field) => (date) => {
        setProductionData(prev => ({
          ...prev,
          [field]: date
        }));
    }, []);

    // 자동출하 토글 핸들러
    const handleAutoShipmentToggle = useCallback((event) => {
      setProductionData(prev => ({
        ...prev,
        autoShipment: event.target.checked
      }));
    }, []);

    // 불량정보 추가
    const addDefectInfo = useCallback(() => {
        setDefectInfos(prev => [
          ...prev,
          {
            id: `defect_${Date.now()}`,
            defectType: '',
            defectQty: 0,
            defectDesc: ''
          }
        ]);
    }, []);

    // 불량정보 제거
    const removeDefectInfo = useCallback((defectId) => {
        setDefectInfos(prev => prev.filter(d => d.id !== defectId));
    }, []);

    // 불량정보 변경
    const updateDefectInfo = useCallback((defectId, field, value) => {
        setDefectInfos(prev =>
          prev.map(d => d.id === defectId ? { ...d, [field]: value } : d)
        );
    }, []);

    // 불량수량이 변경될 때 불량정보 자동 추가/제거
    useEffect(() => {
        const defectQty = Number(productionData.defectQty) || 0;

        if (defectQty > 0 && defectInfos.length === 0) {
          // 불량수량이 있는데 불량정보가 없으면 자동 추가
          addDefectInfo();
        } else if (defectQty === 0) {
          // 불량수량이 0이면 불량정보 모두 제거
          setDefectInfos([]);
        }
    }, [productionData.defectQty, defectInfos.length, addDefectInfo]);


    // 폼 데이터 변경 핸들러
    const handleInputChange = useCallback((field) => (event) => {
        const value = event.target.value;
        setProductionData(prev => ({
          ...prev,
          [field]: value
        }));
    }, []);

    useEffect(()=> {

        if (equipmentOptions.length > 0) {
          const found = initialData?.equipmentId
          ? equipmentOptions.find(e => e.equipmentId === initialData.equipmentId)
          : null;

          const defaultEquipment = found || equipmentOptions[0];
          setSelectedEquipment(defaultEquipment);

          setProductionData(prev => ({
            ...prev,
            equipmentId: defaultEquipment.equipmentId,
            id: `kpi_${defaultEquipment.equipmentId}_${Date.now()}`,
            warehouseId: warehouseOptions.length > 0 ? warehouseOptions[0].value : '',
          }));
        }

    },[initialData, equipmentOptions, warehouseOptions])

    // 화면 로드 시 자동으로 랜덤 데이터 채우기 (대구 지부만)
    useEffect(() => {
      // 대구 지부가 아니면 자동 채우기 실행하지 않음
      if (loginUser?.site !== 'CLUS_DEAGU') {
        return;
      }

      // 이미 자동 채우기가 완료되었거나, 필요한 데이터가 없으면 실행하지 않음
      if (isAutoFilled || !productOptions || productOptions.length === 0) {
        return;
      }

      try {
        // KPI 구독 정보를 기반으로 랜덤 데이터 생성
        const randomData = generateRandomProductionData(kpiSubscriptions);

        // 첫 번째 제품 자동 선택
        const firstProduct = productOptions[0];
        setSelectedProduct(firstProduct);

        // 생산 데이터 업데이트
        setProductionData(prev => ({
          ...prev,
          productId: firstProduct.systemMaterialId,
          goodQty: randomData.goodQty,
          defectQty: randomData.defectQty
        }));

        // 불량정보 설정
        if (randomData.defectInfos && randomData.defectInfos.length > 0) {
          setDefectInfos(randomData.defectInfos);
        }

        // 자동 채우기 완료 플래그 설정
        setIsAutoFilled(true);
      } catch (error) {
        console.error('[Auto-fill] Error generating random data:', error);
      }
    }, [productOptions, kpiSubscriptions, isAutoFilled, loginUser]);

    // 유효성 검증
    useEffect(() => {

        const errors = {};

        // 필수 필드 검증
        if (!productionData.productId) {
          errors.productId = VALIDATION_MESSAGES.REQUIRED_PRODUCT;
        }

        if (!productionData.warehouseId) {
          errors.warehouseId = VALIDATION_MESSAGES.REQUIRED_WAREHOUSE;
        }

        // 수량 검증
        const goodQty = Number(productionData.goodQty) || 0;
        const defectQty = Number(productionData.defectQty) || 0;
        const totalQty = goodQty + defectQty;

        if (totalQty <= 0) {
          errors.quantity = VALIDATION_MESSAGES.INVALID_QUANTITY;
        }

        if (goodQty < 0 || defectQty < 0) {
          errors.quantity = VALIDATION_MESSAGES.NEGATIVE_QUANTITY;
        }

        // 날짜 검증
        if (!productionData.prodStartTime || !productionData.prodEndTime) {
          errors.date = VALIDATION_MESSAGES.REQUIRED_DATE;
        } else if (new Date(productionData.prodStartTime) > new Date(productionData.prodEndTime)) {
          errors.date = VALIDATION_MESSAGES.INVALID_DATE_RANGE;
        }

        // 불량정보 검증
        if (defectQty > 0) {
          const defectInfoQtySum = defectInfos.reduce((sum, info) => sum + (Number(info.defectQty) || 0), 0);
          if (defectInfoQtySum !== defectQty) {
            errors.defectInfo = VALIDATION_MESSAGES.DEFECT_QTY_MISMATCH(defectInfoQtySum, defectQty);
          }

          const hasIncompleteDefectInfo = defectInfos.some(info =>
            !info.defectType || Number(info.defectQty) <= 0
          );
          if (hasIncompleteDefectInfo) {
            errors.defectInfo = VALIDATION_MESSAGES.INCOMPLETE_DEFECT_INFO;
          }
        }

        setValidationErrors(errors);
        setIsValid(Object.keys(errors).length === 0);
    }, [productionData, defectInfos]);


    // 초기화 핸들러
    const handleClear = useCallback(() => {
    const newInitialData = getInitialProductionData();
    setProductionData(newInitialData);
    setSelectedProduct(null);
    setDefectInfos([]);
    }, []);

    // 선택된 제품의 단위 표시
    const getProductUnit = useCallback(() => {
      if (!selectedProduct?.unit) return '';
      const unitOption = unitOptions.find(u => u.value === selectedProduct.unit);
      return unitOption ? unitOption.label : selectedProduct.unit;
    }, [selectedProduct, unitOptions]);

   const handleEquipmentChange = (event) => {
     const equipmentId = event.target.value;
     const equipment = equipmentOptions.find(e => e.equipmentId === equipmentId);
     setSelectedEquipment(equipment || null);
     setProductionData(prev => ({
       ...prev,
       equipmentId: equipmentId || '',
       id: `kpi_${equipmentId}_${Date.now()}`,
     }));
   };

   const buildConfirmHtml = () => {
     const goodQty = Number(productionData.goodQty) || 0;
     const defectQty = Number(productionData.defectQty) || 0;
     const totalAmount = goodQty * (Number(productionData.unitPrice) || 0);

     return `
       <p>설비: <strong>${selectedEquipment?.equipmentName || '-'}</strong></p>
       <p>제품: <strong>${selectedProduct?.materialName +'('+ selectedProduct?.userMaterialId +')' || '-'}</strong></p>
       <p>양품수량: <strong>${goodQty}</strong>      불량수량: <strong>${defectQty}</strong></p>
       <p>생산시작: <strong>${new Date(productionData.prodStartTime).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</strong></p>
       <p>생산종료: <strong>${new Date(productionData.prodEndTime).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</strong></p>
       ${productionData.customerId
       ? `</br> <p><strong>${vendorOptions.find(v => v.value === productionData.customerId)?.label || ''}</strong></p>
       <p>주문일: <strong>${new Date(productionData.orderDate).toLocaleDateString('ko-KR')}</strong></p>
       <p>납기예정일: <strong>${new Date(productionData.deliveryDate).toLocaleDateString('ko-KR')}</strong></p>`
       : ''}
       ${productionData.unitPrice > 0 ? `<p>단가: <strong>${productionData.unitPrice} 원</strong></p>` : ''}
       ${totalAmount > 0 ? `<p>총 금액: <strong>${totalAmount} 원</strong></p>` : ''}
       ${defectQty > 0 ? `<p>불량정보: <strong>${defectInfos.map(d => `${DEFECT_TYPES.find(dt => dt.value === d.defectType)?.label || d.defectType}(${d.defectQty})`).join(', ')}</strong></p>` : ''}
       <br/>
       ${productionData.autoShipment ? `<p>자동출하가 활성화되어 있습니다.</p><p><strong>${new Date(productionData.deliveryDate).toLocaleDateString('ko-KR')}</strong>에 자동으로 출하처리됩니다.</p> ` : ''}
       <p>진행하시겠습니까?</p>
     `;
   };



    const handleSaveClick = useCallback(async () => {
        if (!isValid) {
          Swal.fire({
            icon: 'warning',
            title: '입력 오류',
            text: ALERT_MESSAGES.VALIDATION_ERROR,
            confirmButtonText: '확인'
          });
          return;
        }

        // 생산실적 등록 확인 알림
        const confirmResult = await Swal.fire({
          icon: 'question',
          title: '생산실적 확인',
          html: buildConfirmHtml(),
          showCancelButton: true,
          confirmButtonText: '확인',
          cancelButtonText: '취소',
          confirmButtonColor: '#1976d2',
          cancelButtonColor: '#757575'
        });

        if (!confirmResult.isConfirmed) {
          return; // 사용자가 취소한 경우 저장 중단
        }

        const resultData = {
          ...productionData,
          defectInfos: defectInfos
        };

        try {
          await onSave(productionData.equipmentId, resultData);

          // 자동출하 활성화 시 완료 알림 표시
          if (productionData.autoShipment) {
            const deliveryDateStr = productionData.deliveryDate
              ? new Date(productionData.deliveryDate).toLocaleDateString('ko-KR')
              : '설정된 납기예정일';

            await Swal.fire({
              icon: 'success',
              title: '저장 완료',
              html: `
                <p>생산실적이 성공적으로 저장되었습니다.</p>
                <br/>
                <p><strong>자동출하가 완료되었습니다.</strong></p>
                <p>자동출하일 : ${deliveryDateStr}</p>
              `,
              confirmButtonText: '확인',
              timer: 3000,
              timerProgressBar: true
            });
          }

          // 저장 성공 후에만 폼 자동 초기화
          const newInitialData = getInitialProductionData();
          setProductionData(newInitialData);
          setSelectedProduct(null);
          setDefectInfos([]);
        } catch (error) {
          // 저장 실패 시 폼을 초기화하지 않음 (사용자가 입력한 데이터 보존)
          console.error('저장 실패:', error);
        }
      }, [isValid, productionData, defectInfos, productionData.equipmentId, onSave, warehouseOptions]);



  return (
    <Box sx={{display: 'flex', flexDirection: 'column'}}>
      {/* Form Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, mb: 2}}>
        <Box></Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
           KPI 생산실적등록
        </Typography>
        <IconButton
          onClick={() => setIsHelpModalOpen(true)}
          className="help-icon-button"
          sx={{
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: isDarkMode
                ? alpha(theme.palette.primary.light, 0.1)
                : alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Box>

      {/* kpi 카드 */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      {selectedEquipment && (
        <Card
          className="kpi-equipment-card"
          sx={{
            border: `1px solid ${getBorderColor()}`, mb: 2
          }}
        >
          <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* 선택된 설비 정보 */}
              <Box
                className="equipment-header"
                sx={{
                  bgcolor: '#1976d2',
                  color: 'white',
                  borderRadius: 1,
                }}
              >
                {/* 설비 선택 */}
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <Select
                    value={selectedEquipment?.equipmentId || ""}
                    onChange={handleEquipmentChange}
                    variant="standard"
                    disableUnderline
                    sx={{
                      '& .MuiSelect-select': {
                        color: 'white',
                        fontSize: '1.25rem',
                        backgroundColor: 'rgba(255,255,255,0.15)', // 밝은 반투명 배경
                        borderRadius: 1,
                        p: 1.5, // 좌우 패딩
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                      // hover 시 조금 더 진해지도록
                      '&:hover .MuiSelect-select': {
                        backgroundColor: 'rgba(255,255,255,0.25)',
                      }
                    }}
                  >
                    {equipmentOptions.map(option => (
                      <MenuItem
                        key={option.equipmentId}
                        value={option.equipmentId}
                        sx={{ fontSize: '1.25rem' }}
                      >
                        {option.equipmentName} ({option.equipmentId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* 선택된 장비 상세 */}
                <Typography variant="body2" className="equipment-info">
                  {selectedEquipment?.factoryName} / {selectedEquipment?.lineName} / {selectedEquipment?.equipmentId}
                </Typography>
              </Box>

            {/* 생산실적 입력 폼 */}
            <Box className="production-form" sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 제품 선택 */}
              <Autocomplete
                value={selectedProduct}
                onChange={handleProductChange}
                options={productOptions}
                getOptionLabel={(option) => `${option.userMaterialId} (${option.materialName || ''})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="제품 *"
                    error={!!validationErrors.productId}
                    helperText={validationErrors.productId}
                  />
                )}
                size="small"
              />

              {/* 수량 입력 */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={`양품수량 ${getProductUnit() ? `(${getProductUnit()})` : ''}`}
                    type="number"
                    value={productionData.goodQty}
                    onChange={handleInputChange('goodQty')}
                    error={!!validationErrors.quantity}
                    helperText={validationErrors.quantity}
                    size="small"
                    fullWidth
                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={`불량수량 ${getProductUnit() ? `(${getProductUnit()})` : ''}`}
                    type="number"
                    value={productionData.defectQty}
                    onChange={handleInputChange('defectQty')}
                    error={!!validationErrors.quantity}
                    size="small"
                    fullWidth
                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                  />
                </Grid>
              </Grid>

              {/* 생산 시간 */}
              <Grid container spacing={2}>
                <Grid item xs={6} >
                  <DateTimePicker
                    sx={{ width: '100%' }}
                    label="생산시작일시 *"
                    value={productionData.prodStartTime}
                    onChange={handleDateChange('prodStartTime')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        error={!!validationErrors.date}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DateTimePicker
                    sx={{ width: '100%' }}
                    label="생산종료일시 *"
                    value={productionData.prodEndTime}
                    onChange={handleDateChange('prodEndTime')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        error={!!validationErrors.date}
                        helperText={validationErrors.date}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* KPI 주문정보 입력 */}
              <Box className="order-info-section" sx={{
                bgcolor: '#e8f5e8',
                p: 2,
                borderRadius: 1,
                border: `1px solid ${getBorderColor()}`
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1.5}}>
                  주문정보
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>고객사 (납기처)</InputLabel>
                      <Select
                        value={productionData.customerId}
                        onChange={handleInputChange('customerId')}
                        label="고객사 (납기처)"
                      >
                        <MenuItem value="">
                          <em>선택하세요</em>
                        </MenuItem>
                        {vendorOptions.map((vendor) => (
                          <MenuItem key={vendor.value} value={vendor.value}>
                            {vendor.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={`주문수량 ${getProductUnit() ? `(${getProductUnit()})` : '(개)'}`}
                      type="number"
                      value={Number(productionData.goodQty) || 0}
                      size="small"
                      fullWidth
                      disabled={true}
                      InputProps={{
                        inputProps: { min: 0, step: 1 }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem', mt: 0.5, display: 'block' }}>
                      * 양품수량과 주문수량은 동일하며, 독립생산실적 저장 즉시 주문정보가 생성됩니다
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      label="주문일자"
                      value={productionData.orderDate}
                      onChange={handleDateChange('orderDate')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      label="납기예정일"
                      value={productionData.deliveryDate}
                      onChange={handleDateChange('deliveryDate')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="단가"
                      type="number"
                      value={productionData.unitPrice}
                      onChange={handleInputChange('unitPrice')}
                      size="small"
                      fullWidth
                      placeholder="단가를 입력하세요"
                      InputProps={{
                        inputProps: { min: 0, step: 1 },
                        endAdornment: <Typography variant="caption">원</Typography>
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'red', fontSize: '0.7rem', mt: 0.5, display: 'block' }}>
                      * 개별 품목의 단가이며, 부가세 10%가 자동으로 적용됩니다
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="총 금액"
                      type="number"
                      value={(Number(productionData.goodQty) || 0) * (Number(productionData.unitPrice) || 0)}
                      size="small"
                      fullWidth
                      disabled={true}
                      InputProps={{
                        inputProps: { min: 0, step: 1 },
                        endAdornment: <Typography variant="caption">원</Typography>
                      }}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          fontWeight: 'bold',
                          color: '#d32f2f',
                          WebkitTextFillColor: '#d32f2f'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: isDarkMode ? '#2a2a2a' : '#f0f8ff',
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${productionData.autoShipment ? '#1976d2' : getBorderColor()}`
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: getTextColor() }}>
                          자동출하 설정
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem' }}>
                          {productionData.autoShipment
                            ? '납기예정일에 자동으로 출하처리됩니다'
                            : '수동으로 출하처리해야 합니다'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={productionData.autoShipment}
                            onChange={handleAutoShipmentToggle}
                            color="primary"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>
                    {productionData.autoShipment && (
                      <Typography variant="caption" sx={{
                        color: '#1976d2',
                        fontSize: '0.7rem',
                        mt: 0.5,
                        display: 'block',
                        fontWeight: 500
                      }}>
                        ⚠️ 자동출하가 활성화되면 납기예정일에 바로 출하처리됩니다. 신중히 설정해주세요.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* 불량정보 입력 (불량수량이 있을 때만 표시) */}
              {Number(productionData.defectQty) > 0 && (
                <Box className="defect-info-section" sx={{
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${getBorderColor()}`
                }}>
                  <Box className="defect-info-header" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ErrorOutlineIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      불량정보 등록
                    </Typography>
                  </Box>

                  {validationErrors.defectInfo && (
                    <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                      {validationErrors.defectInfo}
                    </Typography>
                  )}

                  <Box className="defect-info-list" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {defectInfos.map((defectInfo) => (
                      <Box
                        key={defectInfo.id}
                        className="defect-info-item"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                        }}
                      >
                        <FormControl size="small" sx={{ flex: 3, minWidth: 130,}}>
                          <InputLabel>불량유형</InputLabel>
                          <Select
                            value={defectInfo.defectType}
                            onChange={(e) => updateDefectInfo(defectInfo.id, 'defectType', e.target.value)}
                            label="불량유형"
                          >
                            {DEFECT_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          label="수량"
                          type="number"
                          value={defectInfo.defectQty}
                          onChange={(e) => updateDefectInfo(defectInfo.id, 'defectQty', Number(e.target.value))}
                          size="small"
                          sx={{ flex: 2 }}
                          InputProps={{ inputProps: { min: 0, step: 1 } }}
                        />

                        <IconButton
                          size="small"
                          onClick={() => removeDefectInfo(defectInfo.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    startIcon={<AddIcon />}
                    onClick={addDefectInfo}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    불량정보 추가
                  </Button>
                </Box>
              )}

              {/* 버튼 영역 */}
              <Box sx={{
                mt: 2,
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end'
              }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  size="small"
                >
                  초기화
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveClick}
                  size="small"
                  color="primary"
                >
                저장
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      </LocalizationProvider>

      {/* 도움말 모달 */}
      <MobileHelpModal
        open={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title={MobileHelpModalMessages.title}
      >
        {MobileHelpModalMessages.paragraphs.map((paragraph, index) => (
          <Typography key={index} variant="body2" color={getTextColor()} paragraph>
            {paragraph}
          </Typography>
        ))}
      </MobileHelpModal>
    </Box>


  );
};

export default ProductionResultForm;
